import { DetailStatusRow, FilterConfig, FilterOption, IndexStatus, RecentlyIndexedRow } from './types';

/**
 * Response type from the backend API
 */
export type APIResponse = {
    data: IndexStatus[];
    totalDocumentos?: number;
    totalIndexado?: number;
    totalEnProgreso?: number;
    totalListoParaIndexar?: number;
    totalCorrompido?: number;
    totalFaltaOcr?: number;
    totalPdfValidado?: number;
    totalNoDescargado?: number;
    totalNoIndexado?: number;
    detailData?: DetailStatusRow[];
    recentlyIndexed?: RecentlyIndexedRow[];
};

/**
 * Dashboard data structure
 */
export type DashboardData = {
    widgets: {
        totalDocumentos: number;
        totalIndexado: number;
        totalNoIndexado: number;
        totalEnProgreso: number;
        totalListoParaIndexar: number;
        totalCorrompido: number;
        totalFaltaOcr: number;
        totalPdfValidado: number;
        totalNoDescargado: number;
    };
    filters: {
        index_name: string[];
    };
    data: IndexStatus[];
    detailData: DetailStatusRow[];
    recentlyIndexed: RecentlyIndexedRow[];
};

/**
 * Converts API response into the format expected by Dashboard
 */
export function parseAPIResponse(apiResponse: APIResponse): DashboardData {
    console.log('Parsing API response:', apiResponse);

    const data = apiResponse.data || [];

    // Calculate totals from data if not provided
    const totalIndexado = apiResponse.totalIndexado !== undefined
        ? apiResponse.totalIndexado
        : data.reduce((sum, item) => sum + (item.indexado || 0), 0);

    const totalListoParaIndexar = apiResponse.totalListoParaIndexar !== undefined
        ? apiResponse.totalListoParaIndexar
        : data.reduce((sum, item) => sum + (item.listo_para_indexar || 0), 0);

    const totalCorrompido = apiResponse.totalCorrompido !== undefined
        ? apiResponse.totalCorrompido
        : data.reduce((sum, item) => sum + (item.corrompido || 0), 0);

    const totalFaltaOcr = apiResponse.totalFaltaOcr !== undefined
        ? apiResponse.totalFaltaOcr
        : data.reduce((sum, item) => sum + (item.falta_ocr || 0), 0);

    const totalPdfValidado = apiResponse.totalPdfValidado !== undefined
        ? apiResponse.totalPdfValidado
        : data.reduce((sum, item) => sum + (item.pdf_validado || 0), 0);

    const totalNoDescargado = apiResponse.totalNoDescargado !== undefined
        ? apiResponse.totalNoDescargado
        : data.reduce((sum, item) => sum + (item.no_descargado || 0), 0);

    const totalNoIndexado = apiResponse.totalNoIndexado !== undefined
        ? apiResponse.totalNoIndexado
        : totalCorrompido + totalFaltaOcr + totalListoParaIndexar + totalNoDescargado;

    const totalDocumentos = apiResponse.totalDocumentos !== undefined
        ? apiResponse.totalDocumentos
        : totalIndexado + totalNoIndexado;

    const totalEnProgreso = apiResponse.totalEnProgreso !== undefined
        ? apiResponse.totalEnProgreso
        : 0;

    // Parse widgets data
    const widgets = {
        totalDocumentos,
        totalIndexado,
        totalNoIndexado,
        totalEnProgreso,
        totalListoParaIndexar,
        totalCorrompido,
        totalFaltaOcr,
        totalPdfValidado,
        totalNoDescargado
    };

    // No pre-selected filters from API
    const filters = {
        index_name: []
    };

    const result: DashboardData = {
        widgets,
        filters,
        data,
        detailData: apiResponse.detailData || [],
        recentlyIndexed: apiResponse.recentlyIndexed || []
    };

    console.log('Parsed dashboard data:', result);
    return result;
}

/**
 * Extract unique index names from data
 */
function extractUniqueIndexNames(data: IndexStatus[]): FilterOption[] {
    const uniqueValues = new Set<string>();

    data.forEach(row => {
        const value = row.index_name;
        if (value !== undefined && value !== null && value !== '') {
            uniqueValues.add(String(value));
        }
    });

    // Convert to FilterOption array and sort alphabetically
    return Array.from(uniqueValues)
        .sort((a, b) => a.localeCompare(b))
        .map(value => ({ label: value, value }));
}

/**
 * Converts dashboard data into FilterConfig array
 * Dynamically builds filters based on actual data values
 */
export function buildFilterConfigs(dashboardData: DashboardData): FilterConfig[] {
    const filters: FilterConfig[] = [];
    const data = dashboardData.data || [];

    // Extract options from data
    const options: FilterOption[] = extractUniqueIndexNames(data);

    // Only add filter if there are options available
    if (options.length > 0) {
        // Default to first option if no selection
        const defaultSelection = options.length > 0 ? [options[0].value] : [];

        filters.push({
            label: "Index Name",
            options,
            selectedValues: defaultSelection,
            multiSelect: false // Single select only
        });
    }

    return filters;
}
